/**
 * Merge the values from the source object to the target object (the target object will be mutated).
 * This is an alternative to using Object.assign which is not available in ES5.
 * This will fallback to use Object.assign if it's available.
 *
 * @param target - The target object to apply the values to
 * @param sources - The values to apply to the target object
 * @returns The target object with the source values added to it.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign Object.assign()}
 */
export function assign<T>(target: T, ...sources: Array<Partial<T>>): T {
    // Casting to 'any' is to work around the TS Error: Property 'assign' does not exist on type 'ObjectConstructor'
    if (typeof (Object as any).assign === 'function') {
        return (Object as any).assign(target, ...sources);
    } else {
        // Copied from: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#Polyfill
        if (target === null || target === undefined) {
            throw new TypeError('Cannot convert undefined or null to object');
        }

        const to = Object(target);

        for (let index = 0; index < sources.length; index++) {
            const nextSource = sources[index];

            if (nextSource !== null && nextSource !== undefined) {
                for (const nextKey in nextSource) {
                    if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
                        to[nextKey] = nextSource[nextKey];
                    }
                }
            }
        }
        return to;
    }
}
